import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2,
  Users,
  Palette,
  ToggleRight,
  BookOpen,
  BarChart3,
  Plus,
  Search,
  ChevronDown,
  CheckCircle,
  XCircle,
  Loader2,
  Upload,
  RefreshCw,
  Send,
  Shield,
  TrendingUp,
  Activity,
  UserPlus,
  FileSpreadsheet,
  Download,
  GraduationCap,
  ExternalLink,
  Rocket,
  Trash2,
  AlertTriangle,
  HelpCircle,
  Server,
  Globe,
  Settings,
  ArrowRight,
  Copy,
  Check
} from 'lucide-react';
import TenantFeatureManagement from './TenantFeatureManagement';
import BrandingSettings from './BrandingSettings';
import { API } from '../../config/api';

export default function UnifiedTenantManagement() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Data states
  const [organizations, setOrganizations] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [publishedContent, setPublishedContent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  
  // Modal states
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const orgs = response.data.organizations || [];
      setOrganizations(orgs);
      
      // Auto-select first tenant if none selected
      if (!selectedTenant && orgs.length > 0) {
        setSelectedTenant(orgs[0]);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, [token, selectedTenant]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [token]);

  // Fetch published content
  const fetchPublishedContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/master/content/published`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublishedContent(response.data.published_content);
    } catch (error) {
      console.error('Error fetching published content:', error);
    }
  }, [token]);

  // Fetch all courses
  const fetchCourses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, [token]);

  // Fetch users for selected tenant
  const fetchTenantUsers = useCallback(async () => {
    if (!selectedTenant?.org_id) return;
    try {
      const response = await axios.get(`${API}/organizations/${selectedTenant.org_id}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      setTenantUsers([]);
    }
  }, [token, selectedTenant]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOrganizations(),
        fetchAnalytics(),
        fetchPublishedContent(),
        fetchCourses()
      ]);
      setLoading(false);
    };
    
    if (token) {
      loadData();
    }
  }, [token, fetchOrganizations, fetchAnalytics, fetchPublishedContent, fetchCourses]);

  // Fetch tenant users when tenant changes
  useEffect(() => {
    if (selectedTenant) {
      fetchTenantUsers();
    }
  }, [selectedTenant, fetchTenantUsers]);

  const tabs = [
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'features', label: 'Features', icon: ToggleRight },
    { id: 'content', label: 'Content', icon: BookOpen },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'setup-guide', label: 'Setup Guide', icon: HelpCircle },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  // Check if user is super admin
  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
        <p className="text-zinc-400">Super admin access required to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="unified-tenant-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-7 w-7 text-navigate-gold" />
            Tenant Management
          </h1>
          <p className="text-zinc-400 mt-1">
            Configure tenants, users, branding, features, and content publishing
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateTenant(true)} 
          className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          data-testid="create-tenant-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Tenant
        </Button>
      </div>

      {/* Tenant Selector - Always visible except on Tenants tab */}
      {activeTab !== 'tenants' && organizations.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label className="text-zinc-400 text-sm">Configuring:</Label>
              <div className="relative">
                <button
                  onClick={() => setShowTenantSelector(!showTenantSelector)}
                  className="flex items-center gap-3 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                  data-testid="tenant-selector-btn"
                >
                  {selectedTenant?.logo_url ? (
                    <img src={selectedTenant.logo_url} alt="" className="w-6 h-6 rounded object-contain" />
                  ) : (
                    <div className="w-6 h-6 rounded bg-navigate-gold/20 flex items-center justify-center">
                      <Building2 className="h-3 w-3 text-navigate-gold" />
                    </div>
                  )}
                  <span className="text-white font-medium">{selectedTenant?.name || 'Select Tenant'}</span>
                  <ChevronDown className="h-4 w-4 text-zinc-400" />
                </button>
                
                {showTenantSelector && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    {organizations.map(org => (
                      <button
                        key={org.org_id}
                        onClick={() => {
                          setSelectedTenant(org);
                          setShowTenantSelector(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left ${
                          selectedTenant?.org_id === org.org_id ? 'bg-zinc-800' : ''
                        }`}
                      >
                        {org.logo_url ? (
                          <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-contain" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-zinc-700 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-zinc-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{org.name}</p>
                          <p className="text-xs text-zinc-500">{org.slug}.cerebro.co.za</p>
                        </div>
                        {selectedTenant?.org_id === org.org_id && (
                          <CheckCircle className="h-4 w-4 text-navigate-gold ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {selectedTenant && (
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {selectedTenant.current_users || 0} / {selectedTenant.max_users} users
                </span>
                <span className={`flex items-center gap-1 ${selectedTenant.is_active ? 'text-green-400' : 'text-red-400'}`}>
                  {selectedTenant.is_active ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {selectedTenant.is_active ? 'Active' : 'Inactive'}
                </span>
                {/* Launch Button - Preview tenant login */}
                {selectedTenant && (
                  <a
                    href={selectedTenant.custom_domain 
                      ? `https://${selectedTenant.custom_domain}` 
                      : `${window.location.origin}/login?tenant=${selectedTenant.slug || selectedTenant.organization_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-1.5 bg-cerebro-accent hover:bg-cerebro-accent/90 text-cerebro-primary font-semibold rounded-lg transition-colors ml-2"
                    title={selectedTenant.custom_domain ? `Open ${selectedTenant.custom_domain}` : `Preview tenant: ${selectedTenant.slug || selectedTenant.name}`}
                  >
                    <Rocket className="h-4 w-4" />
                    {selectedTenant.custom_domain ? 'Launch' : 'Preview'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-zinc-900 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tenant-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-navigate-gold text-black'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        {activeTab === 'tenants' && (
          <TenantsListTab 
            organizations={organizations} 
            selectedTenant={selectedTenant}
            onSelectTenant={(org) => {
              setSelectedTenant(org);
              setActiveTab('users'); // Navigate to users after selecting
            }}
            onRefresh={fetchOrganizations}
            token={token}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
        
        {activeTab === 'users' && selectedTenant && (
          <UsersTab 
            tenant={selectedTenant}
            users={tenantUsers}
            token={token}
            onRefresh={fetchTenantUsers}
          />
        )}
        
        {activeTab === 'branding' && selectedTenant && (
          <BrandingSettings 
            token={token}
            organizationId={selectedTenant.org_id}
          />
        )}
        
        {activeTab === 'features' && selectedTenant && (
          <TenantFeatureManagement 
            token={token}
            organizationId={selectedTenant.org_id}
            organizationName={selectedTenant.name}
          />
        )}
        
        {activeTab === 'content' && (
          <ContentPublishingTab 
            courses={courses}
            publishedContent={publishedContent}
            organizations={organizations}
            selectedTenant={selectedTenant}
            token={token}
            onRefresh={() => {
              fetchPublishedContent();
              fetchCourses();
            }}
          />
        )}
        
        {activeTab === 'analytics' && (
          <AnalyticsTab 
            analytics={analytics}
            organizations={organizations}
            selectedTenant={selectedTenant}
            token={token}
          />
        )}
        
        {activeTab === 'setup-guide' && (
          <SetupGuideTab />
        )}
        
        {/* No tenant selected warning */}
        {activeTab !== 'tenants' && activeTab !== 'content' && activeTab !== 'analytics' && activeTab !== 'setup-guide' && !selectedTenant && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Tenant Selected</h3>
            <p className="text-zinc-400 mb-4">Select a tenant from the Tenants tab to configure</p>
            <Button onClick={() => setActiveTab('tenants')} variant="outline" className="border-zinc-700">
              Go to Tenants
            </Button>
          </div>
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <CreateTenantModal 
          onClose={() => setShowCreateTenant(false)}
          onSuccess={(newOrg) => {
            fetchOrganizations();
            setSelectedTenant(newOrg);
            setShowCreateTenant(false);
            toast.success('Tenant created successfully!');
          }}
          token={token}
        />
      )}
    </div>
  );
}

// ==================== TENANTS LIST TAB ====================
function TenantsListTab({ organizations, selectedTenant, onSelectTenant, onRefresh, token, searchTerm, setSearchTerm }) {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const filteredOrgs = organizations.filter(org => 
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteTenant = async (org, permanent = false) => {
    // Prevent deleting master org
    if (org.org_id === 'org_navigate_master') {
      toast.error('Cannot delete the master organization');
      return;
    }
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/master/organizations/${org.org_id}?permanent=${permanent}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(permanent ? `${org.name} permanently deleted` : `${org.name} deactivated`);
      setDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">All Tenants ({organizations.length})</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tenants..."
              className="pl-10 w-64 bg-zinc-800 border-zinc-700"
            />
          </div>
          <Button variant="outline" onClick={onRefresh} className="border-zinc-700">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tenants Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOrgs.map(org => (
          <div 
            key={org.org_id}
            className={`p-4 rounded-xl border transition-all hover:shadow-lg ${
              selectedTenant?.org_id === org.org_id 
                ? 'bg-navigate-gold/10 border-navigate-gold/50' 
                : 'bg-zinc-800/50 border-zinc-700 hover:bg-zinc-800'
            }`}
          >
            <div className="flex items-start gap-4">
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => onSelectTenant(org)}
              >
                <div className="flex items-start gap-4">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-zinc-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{org.name}</h3>
                    <p className="text-sm text-zinc-500 truncate">{org.slug}.cerebro.co.za</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        org.org_type === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                        org.org_type === 'smb' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-zinc-700 text-zinc-300'
                      }`}>
                        {org.org_type}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {org.current_users || 0}/{org.max_users} users
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Status and Actions */}
              <div className="flex flex-col items-end gap-2">
                <div className={`w-2 h-2 rounded-full ${org.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                
                {/* Delete button - don't show for master org */}
                {org.org_id !== 'org_navigate_master' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(org);
                    }}
                    className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    title="Delete tenant"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOrgs.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No tenants found</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-md">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="h-6 w-6" />
                <h2 className="text-xl font-bold">Delete Tenant</h2>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-zinc-300 mb-4">
                Are you sure you want to delete <strong className="text-white">{deleteConfirm.name}</strong>?
              </p>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-zinc-400 mb-2">This tenant has:</p>
                <ul className="text-sm text-zinc-300 space-y-1">
                  <li>• {deleteConfirm.current_users || 0} users</li>
                  <li>• Domain: {deleteConfirm.slug}.cerebro.co.za</li>
                </ul>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleDeleteTenant(deleteConfirm, false)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Deactivate (Recoverable)
                </button>
                
                <button
                  onClick={() => handleDeleteTenant(deleteConfirm, true)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Permanently Delete
                </button>
                
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="w-full px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== USERS TAB ====================
function UsersTab({ tenant, users, token, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [manualUsers, setManualUsers] = useState([
    { email: '', full_name: '', job_title: '', department: '' }
  ]);

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axios.post(
        `${API}/organizations/${tenant.org_id}/users/upload-csv`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setResults(response.data);
      toast.success(`${response.data.summary.created} users created`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const addManualRow = () => {
    setManualUsers([...manualUsers, { email: '', full_name: '', job_title: '', department: '' }]);
  };

  const updateManualUser = (index, field, value) => {
    const updated = [...manualUsers];
    updated[index][field] = value;
    setManualUsers(updated);
  };

  const handleManualUpload = async () => {
    const validUsers = manualUsers.filter(u => u.email && u.full_name);
    if (validUsers.length === 0) {
      toast.error('Please add at least one user with email and name');
      return;
    }

    try {
      setUploading(true);
      const response = await axios.post(
        `${API}/organizations/${tenant.org_id}/users/bulk`,
        { users: validUsers, send_welcome_email: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setResults(response.data);
      toast.success(`${response.data.summary.created} users created`);
      setManualUsers([{ email: '', full_name: '', job_title: '', department: '' }]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create users');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,full_name,job_title,department\njohn@example.com,John Doe,Compliance Officer,Compliance\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_upload_template.csv';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <Users className="h-5 w-5 text-blue-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.length}</p>
          <p className="text-xs text-zinc-400">Total Users</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <GraduationCap className="h-5 w-5 text-green-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.filter(u => u.is_active).length}</p>
          <p className="text-xs text-zinc-400">Active Learners</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <TrendingUp className="h-5 w-5 text-purple-400 mb-2" />
          <p className="text-2xl font-bold text-white">{users.reduce((sum, u) => sum + (u.completed_count || 0), 0)}</p>
          <p className="text-xs text-zinc-400">Completions</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <Activity className="h-5 w-5 text-orange-400 mb-2" />
          <p className="text-2xl font-bold text-white">{(tenant.max_users || 100) - users.length}</p>
          <p className="text-xs text-zinc-400">Seats Available</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input 
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700"
          />
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowBulkUpload(!showBulkUpload)}
            className="border-zinc-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload
          </Button>
          <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Bulk Upload Section */}
      {showBulkUpload && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* CSV Upload */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-green-400" />
                Upload CSV
              </h4>
              <label className="block">
                <div className="border-2 border-dashed border-zinc-600 rounded-lg p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors">
                  <Upload className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">
                    {uploading ? 'Uploading...' : 'Click to upload CSV'}
                  </p>
                </div>
                <input 
                  type="file" 
                  accept=".csv"
                  onChange={handleCSVUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <Button 
                variant="outline" 
                onClick={downloadTemplate}
                className="mt-3 border-zinc-700 text-zinc-300 w-full"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* Manual Entry */}
            <div>
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-400" />
                Manual Entry
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {manualUsers.map((user, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Email *"
                      value={user.email}
                      onChange={(e) => updateManualUser(idx, 'email', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-sm"
                    />
                    <Input
                      placeholder="Full Name *"
                      value={user.full_name}
                      onChange={(e) => updateManualUser(idx, 'full_name', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-sm"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={addManualRow} size="sm" className="border-zinc-700">
                  <Plus className="h-3 w-3 mr-1" />
                  Add Row
                </Button>
                <Button 
                  onClick={handleManualUpload}
                  disabled={uploading}
                  size="sm"
                  className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                >
                  Create Users
                </Button>
              </div>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="border-t border-zinc-700 pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{results.summary.created}</p>
                  <p className="text-xs text-zinc-400">Created</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">{results.summary.skipped}</p>
                  <p className="text-xs text-zinc-400">Skipped</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{results.summary.failed}</p>
                  <p className="text-xs text-zinc-400">Failed</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-zinc-800/30 border border-zinc-700 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">User</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Department</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Enrollments</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Completed</th>
              <th className="text-left p-4 text-xs font-medium text-zinc-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.user_id} className="border-t border-zinc-700 hover:bg-zinc-800/50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-xs">
                        {user.full_name?.split(' ').map(n => n[0]).join('') || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{user.full_name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-sm text-zinc-400">{user.department || '—'}</td>
                <td className="p-4 text-sm text-zinc-300">{user.enrollments_count || 0}</td>
                <td className="p-4 text-sm text-green-400">{user.completed_count || 0}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded ${
                    user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-zinc-500">
            No users found
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CONTENT PUBLISHING TAB ====================
function ContentPublishingTab({ courses, publishedContent, organizations, selectedTenant, token, onRefresh }) {
  const [publishing, setPublishing] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [publishScope, setPublishScope] = useState('all');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [mandatory, setMandatory] = useState(false);

  const publishedCourseIds = (publishedContent?.course || []).map(c => c.course_id);
  const publishedCourses = courses.filter(c => c.is_published);

  const handlePublish = async () => {
    if (!selectedCourse) return;
    
    setPublishing(true);
    try {
      await axios.post(
        `${API}/master/content/course/${selectedCourse.course_id}/publish`,
        {
          publish_scope: publishScope,
          tenant_ids: publishScope === 'selected' ? selectedTenants : null,
          mandatory: mandatory,
          auto_enroll_new_users: false,
          sync_updates: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Course published to tenants!');
      setSelectedCourse(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish course');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Publish Form */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Send className="h-5 w-5 text-navigate-gold" />
          Publish Content to Tenants
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-zinc-300 mb-2 block">Select Course</Label>
            <select
              value={selectedCourse?.course_id || ''}
              onChange={(e) => {
                const course = courses.find(c => c.course_id === e.target.value);
                setSelectedCourse(course);
              }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white"
            >
              <option value="">Choose a course...</option>
              {publishedCourses.map(course => (
                <option key={course.course_id} value={course.course_id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-zinc-300 mb-2 block">Publish To</Label>
            <select
              value={publishScope}
              onChange={(e) => setPublishScope(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white"
            >
              <option value="all">All Tenants</option>
              <option value="selected">Selected Tenants</option>
            </select>
          </div>

          {publishScope === 'selected' && (
            <div className="md:col-span-2">
              <Label className="text-zinc-300 mb-2 block">Select Tenants</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-3 bg-zinc-900 rounded-lg border border-zinc-700">
                {organizations.map(org => (
                  <label key={org.org_id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTenants.includes(org.org_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTenants([...selectedTenants, org.org_id]);
                        } else {
                          setSelectedTenants(selectedTenants.filter(id => id !== org.org_id));
                        }
                      }}
                      className="rounded"
                    />
                    {org.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={mandatory}
                onChange={(e) => setMandatory(e.target.checked)}
                className="rounded"
              />
              Mandatory (tenants cannot disable this course)
            </label>
          </div>

          <div className="md:col-span-2">
            <Button
              onClick={handlePublish}
              disabled={!selectedCourse || publishing}
              className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              {publishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Publish to {publishScope === 'all' ? 'All Tenants' : `${selectedTenants.length} Tenants`}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Published Content List */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-green-400" />
          Published to Tenants ({publishedContent?.course?.length || 0})
        </h3>
        
        <div className="space-y-3">
          {(publishedContent?.course || []).map(course => (
            <div key={course.course_id} className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
              <div>
                <p className="text-white font-medium">{course.title}</p>
                <p className="text-sm text-zinc-500">
                  {course.publish_scope === 'all' ? 'All tenants' : 'Selected tenants'} 
                  {' '}• {course.active_tenant_count} active
                </p>
              </div>
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                Published
              </span>
            </div>
          ))}
          {(!publishedContent?.course || publishedContent.course.length === 0) && (
            <p className="text-zinc-500 text-center py-8">No published courses yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== ANALYTICS TAB ====================
function AnalyticsTab({ analytics, organizations, selectedTenant, token }) {
  const overview = analytics?.overview || {};
  
  return (
    <div className="space-y-6">
      {/* Platform Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Platform Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Building2 className="h-5 w-5 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_tenants || 0}</p>
            <p className="text-sm text-zinc-400">Total Tenants</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Users className="h-5 w-5 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_users || 0}</p>
            <p className="text-sm text-zinc-400">Total Users</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <BookOpen className="h-5 w-5 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.total_enrollments || 0}</p>
            <p className="text-sm text-zinc-400">Enrollments</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-5">
            <Activity className="h-5 w-5 text-orange-400 mb-2" />
            <p className="text-2xl font-bold text-white">{overview.completion_rate || 0}%</p>
            <p className="text-sm text-zinc-400">Completion Rate</p>
          </div>
        </div>
      </div>

      {/* Top Tenants */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-navigate-gold" />
            Top Tenants by Users
          </h3>
          <div className="space-y-3">
            {(analytics?.top_tenants || []).slice(0, 5).map((tenant, idx) => (
              <div key={tenant.org_id} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-navigate-gold text-black' : 'bg-zinc-700 text-white'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-white">{tenant.name}</span>
                </div>
                <span className="text-zinc-400">{tenant.user_count} users</span>
              </div>
            ))}
            {(!analytics?.top_tenants || analytics.top_tenants.length === 0) && (
              <p className="text-zinc-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-navigate-gold" />
            Tenants by Type
          </h3>
          <div className="space-y-3">
            {Object.entries(analytics?.tenants_by_type || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                <span className="text-white capitalize">{type.replace('_', ' ')}</span>
                <span className="px-3 py-1 bg-zinc-700 rounded-full text-sm text-white">{count}</span>
              </div>
            ))}
            {Object.keys(analytics?.tenants_by_type || {}).length === 0 && (
              <p className="text-zinc-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== CREATE TENANT MODAL ====================
function CreateTenantModal({ onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    custom_domain: '',
    org_type: 'enterprise',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    city: '',
    country: 'South Africa',
    max_users: 100
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Either slug or custom_domain must be provided
    if (!formData.slug.trim() && !formData.custom_domain.trim()) {
      toast.error('Please provide either a subdomain or a custom domain');
      return;
    }
    
    setLoading(true);
    
    try {
      // Only send slug if it has a value
      const payload = {
        ...formData,
        slug: formData.slug.trim() || null,
        custom_domain: formData.custom_domain.trim() || null
      };
      
      const response = await axios.post(
        `${API}/master/organizations`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="h-5 w-5 text-navigate-gold" />
            Create New Tenant
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-zinc-300">Organization Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ACME Financial Services"
              className="mt-1 bg-zinc-800 border-zinc-700"
              required
            />
          </div>
          
          <div>
            <Label className="text-zinc-300">
              Slug (Subdomain) {!formData.custom_domain.trim() && !formData.slug.trim() && <span className="text-amber-500">*</span>}
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="acme-financial"
                className="bg-zinc-800 border-zinc-700"
              />
              <span className="text-zinc-500 text-sm whitespace-nowrap">.cerebro.co.za</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {formData.custom_domain.trim() 
                ? "Optional when using custom domain" 
                : "Required if no custom domain is provided"}
            </p>
          </div>
          
          <div>
            <Label className="text-zinc-300">
              Custom Domain {!formData.slug.trim() && !formData.custom_domain.trim() && <span className="text-amber-500">*</span>}
            </Label>
            <Input
              value={formData.custom_domain}
              onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase() })}
              placeholder="learning.acme.com"
              className="mt-1 bg-zinc-800 border-zinc-700"
            />
            <p className="text-xs text-zinc-500 mt-1">
              {formData.slug.trim()
                ? "Optional when using subdomain"
                : formData.custom_domain.trim()
                  ? "Client must configure DNS CNAME to point to this app"
                  : "Required if no subdomain is provided"}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">Type</Label>
              <select
                value={formData.org_type}
                onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
              >
                <option value="enterprise">Enterprise</option>
                <option value="smb">SMB</option>
                <option value="trial">Trial</option>
              </select>
            </div>
            <div>
              <Label className="text-zinc-300">Max Users</Label>
              <Input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-zinc-300">Contact Email *</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              placeholder="admin@acme.com"
              className="mt-1 bg-zinc-800 border-zinc-700"
              required
            />
          </div>
          
          <div>
            <Label className="text-zinc-300">Contact Name</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="John Smith"
              className="mt-1 bg-zinc-800 border-zinc-700"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Johannesburg"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-zinc-700">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-navigate-gold hover:bg-navigate-gold/90 text-black">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== SETUP GUIDE TAB ====================
function SetupGuideTab() {
  const [copiedText, setCopiedText] = useState('');
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const renderCopyButton = (text, label) => (
    <button
      onClick={() => copyToClipboard(text, label)}
      className="ml-2 p-1 text-zinc-500 hover:text-white transition-colors"
      title="Copy to clipboard"
    >
      {copiedText === label ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-zinc-800 pb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <HelpCircle className="h-6 w-6 text-navigate-gold" />
          Tenant Setup &amp; Deployment Guide
        </h2>
        <p className="text-zinc-400 mt-2">
          Complete guide for creating tenants, configuring staging environments, and deploying to production.
        </p>
      </div>

      {/* Step 1: Create Tenant */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">1</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Create a New Tenant</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Click the <strong className="text-navigate-gold">&quot;New Tenant&quot;</strong> button and fill in the required fields:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Organization Name</p>
                    <p className="text-sm text-zinc-400">The display name for the tenant (e.g., &quot;ACME Financial Services&quot;)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Subdomain (Slug) OR Custom Domain</p>
                    <p className="text-sm text-zinc-400">
                      <strong>Option A:</strong> Use a subdomain like <code className="bg-zinc-800 px-1 rounded">acme.cerebro.co.za</code><br/>
                      <strong>Option B:</strong> Use client&apos;s custom domain like <code className="bg-zinc-800 px-1 rounded">learning.acme.com</code>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-zinc-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Max Users &amp; Contact Details</p>
                    <p className="text-sm text-zinc-400">Set user limits and provide administrator contact information</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Configure Microsoft SSO */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">2</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Configure Microsoft Entra ID SSO</h3>
            <div className="space-y-4 text-zinc-300">
              <p>For enterprise users to login via Microsoft, configure Entra ID in <strong>Azure Portal</strong>:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-amber-400">Required Azure App Registration Settings:</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                    <span className="text-zinc-400">Client ID:</span>
                    <code className="text-white">AZURE_AD_CLIENT_ID</code>
                    {renderCopyButton("AZURE_AD_CLIENT_ID", "client-id")}
                  </div>
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                    <span className="text-zinc-400">Client Secret:</span>
                    <code className="text-white">AZURE_AD_CLIENT_SECRET</code>
                    {renderCopyButton("AZURE_AD_CLIENT_SECRET", "client-secret")}
                  </div>
                  <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                    <span className="text-zinc-400">Redirect URI:</span>
                    <code className="text-white">https://your-app.azurewebsites.net/api/v1/auth/sso/callback</code>
                    {renderCopyButton("https://your-app.azurewebsites.net/api/v1/auth/sso/callback", "redirect-uri")}
                  </div>
                </div>
                
                <p className="text-xs text-zinc-500 mt-2">
                  Add these as environment variables in Azure App Service → Configuration → Application settings
                </p>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300">
                  <strong>Note:</strong> Each enterprise tenant needs their Entra Tenant ID configured in the tenant settings
                  to enable domain-based SSO routing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Staging Environment */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">3</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Set Up Staging Environment</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Staging allows you to test changes before affecting production users.</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-4">
                <div>
                  <p className="font-medium text-white mb-2">A. Create Staging Slot in Azure Portal:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                    <li>Navigate to <strong>App Service</strong> → Your app</li>
                    <li>Go to <strong>Deployment → Deployment slots</strong></li>
                    <li>Click <strong>+ Add Slot</strong></li>
                    <li>Name it <code className="bg-zinc-800 px-1 rounded">staging</code></li>
                    <li>Clone settings from production (optional)</li>
                  </ol>
                </div>
                
                <div>
                  <p className="font-medium text-white mb-2">B. Configure Staging Environment Variables:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Environment:</span>
                      <code className="text-white">ENVIRONMENT=staging</code>
                      {renderCopyButton("ENVIRONMENT=staging", "env-staging")}
                    </div>
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Database:</span>
                      <code className="text-white">DB_NAME=cerebro_staging</code>
                      {renderCopyButton("DB_NAME=cerebro_staging", "db-staging")}
                    </div>
                    <div className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                      <span className="text-zinc-400">Admin Access:</span>
                      <code className="text-white">ADMIN_ACCESS_TOKEN=&lt;your-token&gt;</code>
                      {renderCopyButton("ADMIN_ACCESS_TOKEN=", "admin-token")}
                    </div>
                  </div>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-sm text-amber-300">
                    <strong>Important:</strong> Use a separate database (cerebro_staging) for staging to avoid affecting production data.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-medium text-white mb-2">Staging URL Pattern:</p>
                <code className="bg-zinc-800 px-3 py-2 rounded block text-sm">
                  https://your-app-staging.azurewebsites.net
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 4: Deploy to Staging */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">4</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Deploy to Staging</h3>
            <div className="space-y-4 text-zinc-300">
              <p>Use GitHub Actions for safe, automated deployments:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Push changes to the <code className="bg-zinc-800 px-1 rounded">main</code> branch on GitHub</li>
                  <li>Go to <strong>GitHub → Actions → &quot;Deploy to Azure App Service&quot;</strong></li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong></li>
                  <li>Select <strong>deploy_target: staging</strong> (default)</li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong> to start deployment</li>
                </ol>
                
                <div className="flex items-center gap-2 mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <p className="text-sm text-green-300">
                    Staging deployment is the <strong>default</strong> — it will not accidentally deploy to production.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-medium text-white mb-2">After deployment, verify:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Health check passes at <code className="bg-zinc-800 px-1 rounded">/api/health</code></li>
                  <li>Admin login works with your access token</li>
                  <li>Tenant branding displays correctly</li>
                  <li>Microsoft SSO redirects properly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 5: Promote to Production */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">5</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Promote to Production</h3>
            <div className="space-y-4 text-zinc-300">
              <p>After validating staging, promote to production:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="font-medium text-white">Option A: Slot Swap (Recommended - Zero Downtime)</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to Azure Portal → App Service → Deployment slots</li>
                  <li>Click <strong>&quot;Swap&quot;</strong></li>
                  <li>Source: <code className="bg-zinc-800 px-1 rounded">staging</code> → Target: <code className="bg-zinc-800 px-1 rounded">production</code></li>
                  <li>Review changes and confirm</li>
                </ol>
                
                <div className="border-t border-zinc-700 my-4"></div>
                
                <p className="font-medium text-white">Option B: Direct Deploy via GitHub Actions</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to GitHub → Actions → &quot;Deploy to Azure App Service&quot;</li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong></li>
                  <li>Select <strong>deploy_target: production</strong></li>
                  <li>Type <strong className="text-red-400">DEPLOY</strong> in the confirmation field</li>
                  <li>Click <strong>&quot;Run workflow&quot;</strong></li>
                </ol>
                
                <div className="flex items-center gap-2 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  <p className="text-sm text-red-300">
                    Production requires typing <strong>&quot;DEPLOY&quot;</strong> to confirm. This prevents accidental deployments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 6: Custom Domain Setup */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navigate-gold/20 flex items-center justify-center">
            <span className="text-navigate-gold font-bold">6</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Configure Custom Domain (Optional)</h3>
            <div className="space-y-4 text-zinc-300">
              <p>If the tenant uses a custom domain like <code className="bg-zinc-800 px-1 rounded">learning.acme.com</code>:</p>
              
              <div className="bg-zinc-900 rounded-lg p-4 space-y-3">
                <p className="font-medium text-white">Client&apos;s DNS Configuration:</p>
                <div className="bg-zinc-800 px-3 py-2 rounded text-sm font-mono">
                  <span className="text-zinc-400">CNAME</span> <span className="text-white">learning.acme.com</span> → <span className="text-green-400">your-app.azurewebsites.net</span>
                </div>
                
                <p className="font-medium text-white mt-4">Azure App Service Configuration:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-400 ml-4">
                  <li>Go to App Service → Custom domains</li>
                  <li>Click <strong>&quot;+ Add custom domain&quot;</strong></li>
                  <li>Enter the domain and validate</li>
                  <li>Add SSL binding (use App Service Managed Certificate for free SSL)</li>
                </ol>
                
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mt-3">
                  <p className="text-sm text-blue-300">
                    <strong>Tip:</strong> The tenant&apos;s custom domain must match what&apos;s configured in their tenant settings for proper routing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tenant Launch Checklist */}
      <div className="bg-zinc-800/50 rounded-xl p-6 border border-green-500/30">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-3">Tenant Launch Checklist</h3>
            <p className="text-zinc-400 mb-4">Complete these steps before launching a tenant to users:</p>
            
            <div className="space-y-4">
              {/* Phase 1: Basic Setup */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-amber-400 mb-3">Phase 1: Basic Setup (Required)</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">1</div>
                    <span className="text-zinc-300">Create tenant with name and slug/domain</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">2</div>
                    <span className="text-zinc-300">Configure branding (logo, colors, login background)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">3</div>
                    <span className="text-zinc-300">Enable/disable features for the tenant</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">4</div>
                    <span className="text-zinc-300">Create at least one course in the Content tab</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">5</div>
                    <span className="text-zinc-300">Publish courses to the tenant</span>
                  </div>
                </div>
              </div>

              {/* Phase 2: Authentication */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-blue-400 mb-3">Phase 2: User Authentication</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">6</div>
                    <span className="text-zinc-300">Configure Microsoft Entra ID SSO in Azure Portal</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">7</div>
                    <span className="text-zinc-300">Set tenant&apos;s Entra Tenant ID in tenant settings</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">8</div>
                    <span className="text-zinc-300">Test SSO login with a user from the tenant&apos;s organization</span>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300">
                  <strong>When is SSO needed?</strong> SSO is required for production launch. Without it, users cannot login. 
                  For testing, use the Admin Access Token to access the platform.
                </div>
              </div>

              {/* Phase 3: Custom Domain */}
              <div className="bg-zinc-900 rounded-lg p-4">
                <p className="font-medium text-purple-400 mb-3">Phase 3: Custom Domain (Optional)</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">9</div>
                    <span className="text-zinc-300">Client creates CNAME record pointing to your app</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">10</div>
                    <span className="text-zinc-300">Add custom domain in Azure App Service</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">11</div>
                    <span className="text-zinc-300">Configure SSL certificate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border border-zinc-600 flex items-center justify-center text-xs">12</div>
                    <span className="text-zinc-300">Update tenant settings with custom domain</span>
                  </div>
                </div>
              </div>

              {/* Ready Indicators */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="font-medium text-green-400 mb-2">How to know when a tenant is ready:</p>
                <ul className="space-y-1 text-sm text-zinc-300 list-disc list-inside ml-2">
                  <li>Tenant appears in the Tenants list with &quot;Active&quot; status</li>
                  <li>At least one course is published to the tenant</li>
                  <li>Branding is configured (logo displays on login page)</li>
                  <li>SSO is configured and tested (for production)</li>
                  <li>Preview/Launch button shows the tenant&apos;s branded login page</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Reference */}
      <div className="bg-gradient-to-r from-navigate-gold/10 to-transparent rounded-xl p-6 border border-navigate-gold/30">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-navigate-gold" />
          Quick Reference - Required Environment Variables
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">Authentication</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">ADMIN_ACCESS_TOKEN=</span><span className="text-white">&lt;32-char-token&gt;</span></p>
              <p><span className="text-zinc-500">JWT_SECRET=</span><span className="text-white">&lt;secure-secret&gt;</span></p>
              <p><span className="text-zinc-500">AZURE_AD_CLIENT_ID=</span><span className="text-white">&lt;guid&gt;</span></p>
              <p><span className="text-zinc-500">AZURE_AD_CLIENT_SECRET=</span><span className="text-white">&lt;secret&gt;</span></p>
            </div>
          </div>
          <div className="bg-zinc-900/50 rounded-lg p-4">
            <p className="font-medium text-navigate-gold mb-2">Database &amp; Storage</p>
            <div className="space-y-1 text-sm font-mono">
              <p><span className="text-zinc-500">MONGO_URL=</span><span className="text-white">&lt;cosmos-connection&gt;</span></p>
              <p><span className="text-zinc-500">DB_NAME=</span><span className="text-white">cerebro_production</span></p>
              <p><span className="text-zinc-500">AZURE_STORAGE_CONNECTION_STRING=</span><span className="text-white">&lt;blob-conn&gt;</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}